import React, { useRef, useEffect, useState, useMemo } from 'react';
import get from 'lodash/get';
import { useLazyQuery } from '@apollo/react-hooks';
import { Field, ErrorMessage } from '@atlaskit/form';
import Select from '@atlaskit/select';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import type { Project } from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import { FieldContainer } from '@atlassian/jira-issue-create-commons/src/common/ui/fields/styled.tsx';
import { transformStargateUrl } from '@atlassian/jira-issue-create-commons/src/common/utils/transform-stargate-url/index.tsx';
import { useFormContext } from '@atlassian/jira-issue-create-form-context/src/index.tsx';
import { formatOptionLabel } from '@atlassian/jira-issue-field-select-base/src/ui/format-option-label/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { transformProjects } from '../../../../common/utils/context-fields/index.tsx';
import {
	scrollToElement,
	preventEscKeyPropagationKeyPressHandler,
} from '../../../../common/utils/index.tsx';
import { PROJECT_PICKER_ELEMENT_ID } from './constants.tsx';
import { PROJECTS_LIST_QUERY_WITHOUT_CLASSIFICATION } from './gql.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';
import { getFilteredOptions } from './utils.tsx';

const ProjectPicker = (props: Props) => {
	const {
		onChange,
		value: selectedProjectValue,
		renderOptionsLimit,
		skipFetchProjects = false,
		isDisabled = false,
		disableFetchProject = false,
		width,
		onProjectSelection,
	} = props;
	const [
		{
			meta: { submitFieldwiseErrors },
		},
	] = useFormContext();
	const [recentProjects, setRecentProjects] = useState<Project[]>([]);
	const [filterQuery, setFilterQuery] = useState<string>('');
	const [allProjects, setAllProjects] = useState<Project[]>([]);
	const [isLoading, setLoading] = useState<boolean>(true);
	const { formatMessage } = useIntl();
	const shouldFetchProjects = useRef<boolean>(true);
	const [fetchProjects] = useLazyQuery(PROJECTS_LIST_QUERY_WITHOUT_CLASSIFICATION, {
		fetchPolicy: 'no-cache',
		onCompleted: (data) => {
			setRecentProjects(transformProjects(get(data, 'recentProjects.nodes', [])));
			setAllProjects(transformProjects(get(data, 'allProjects.nodes', [])));
			setLoading(false);
		},
	});
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onProjectPickerInputChange = (query: string) => {
		setFilterQuery(query);
	};

	useEffect(() => {
		if ((selectedProjectValue && shouldFetchProjects.current) || skipFetchProjects) {
			shouldFetchProjects.current = false;
			disableFetchProject ? setLoading(false) : fetchProjects();
		}
		if (selectedProjectValue) {
			onProjectSelection?.(selectedProjectValue);
		}
	}, [
		fetchProjects,
		selectedProjectValue,
		skipFetchProjects,
		disableFetchProject,
		onProjectSelection,
	]);

	useEffect(() => {
		if (submitFieldwiseErrors?.project) {
			scrollToElement(PROJECT_PICKER_ELEMENT_ID);
		}
	}, [submitFieldwiseErrors?.project]);

	const onOptionSelect = (project: Project): void => {
		if (project?.value === projectValue?.value) {
			return;
		}
		const analyticsEvent = createAnalyticsEvent({
			action: 'selected',
			actionSubject: 'option',
		});
		fireUIAnalytics(analyticsEvent, 'project');
		onChange(project);
	};

	const filteredOptions = useMemo(() => {
		const { filteredAllProjects, filteredRecentProjects } = getFilteredOptions({
			recentProjects,
			allProjects,
			filterQuery,
			renderOptionsLimit,
		});

		return [
			{
				label: formatMessage(
					expVal('project-terminology-refresh', 'isEnabled', false)
						? messages.recentProjectsGalaxia
						: messages.recentProjects,
				),
				options: filteredRecentProjects,
			},
			{
				label: formatMessage(
					expVal('project-terminology-refresh', 'isEnabled', false)
						? messages.allProjectsGalaxia
						: messages.allProjects,
				),
				options: filteredAllProjects,
			},
		] as const;
	}, [recentProjects, allProjects, filterQuery, formatMessage, renderOptionsLimit]);

	const projectValue = useMemo(
		() =>
			selectedProjectValue && {
				...selectedProjectValue,
				iconUrl: transformStargateUrl(selectedProjectValue?.iconUrl || ''),
			},
		[selectedProjectValue],
	);

	return (
		<FieldContainer width={width}>
			<Field
				name="project"
				label={formatMessage(
					expVal('project-terminology-refresh', 'isEnabled', false)
						? messages.projectFieldLabelGalaxia
						: messages.projectFieldLabel,
				)}
				isRequired
			>
				{({ fieldProps: { id, isRequired } }) => (
					<>
						<Select
							inputId={id}
							required={isRequired}
							options={filteredOptions}
							onInputChange={onProjectPickerInputChange}
							filterOption={null}
							// @ts-expect-error - TS2322 - Type '(project: Project) => void' is not assignable to type '(value: Option | null, action: ActionMeta<Option>) => void'.
							onChange={onOptionSelect}
							value={projectValue}
							isClearable={false}
							formatOptionLabel={formatOptionLabel}
							hideSelectedOptions={false}
							placeholder=""
							isLoading={isLoading}
							isDisabled={isDisabled}
							id={PROJECT_PICKER_ELEMENT_ID}
							onKeyDown={preventEscKeyPropagationKeyPressHandler}
						/>
						{submitFieldwiseErrors?.project && (
							<ErrorMessage>{submitFieldwiseErrors.project}</ErrorMessage>
						)}
					</>
				)}
			</Field>
		</FieldContainer>
	);
};

export default ProjectPicker;
